/**
 * The two computations this screen makes: the window the reader picked as a
 * pair of instants, and a headline figure's delta from the two numbers the
 * route stated. Everything else on the screen is read, not derived.
 */

import type { Locale } from '../../../i18n';
import { score } from '../../../chrome/measure/format';
import type { Trend } from '../../../chrome/chart';
import type { Window } from '../api/queries';

export const SPANS = ['6h', '1d', '3d', '7d'] as const;
export type Span = (typeof SPANS)[number];

const HOUR_MS = 3_600_000;
const SPAN_MS: Record<Span, number> = {
  '6h': 6 * HOUR_MS,
  '1d': 24 * HOUR_MS,
  '3d': 72 * HOUR_MS,
  '7d': 168 * HOUR_MS,
};

export interface Range {
  window: Window;
  /** The window of the same length that ends where `window` starts: what each
   *  figure's `previous` is measured over. */
  compareTo: Window;
}

export function rangeFor(span: Span, now: Date): Range {
  const to = now.getTime();
  const from = to - SPAN_MS[span];
  const iso = (ms: number): string => new Date(ms).toISOString();
  return {
    window: { from: iso(from), to: iso(to) },
    compareTo: { from: iso(from - SPAN_MS[span]), to: iso(from) },
  };
}

export type DeltaKind = 'percent' | 'points' | 'count';

export interface Delta {
  text: string;
  trend: Trend;
}

function trendOf(rounded: number): Trend {
  return rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
}

function signed(formatted: string): string {
  return formatted.replace(/^-/, '−');
}

/**
 * A figure's change against the window before it. `null` when either number is
 * absent, and for a percentage against a `previous` of zero: no reading is not
 * a change of zero, and a ratio to nothing is not a number.
 */
export function deltaOf(
  figure: { value: number | null; previous: number | null },
  kind: DeltaKind,
  locale: Locale,
): Delta | null {
  const { value, previous } = figure;
  if (value === null || previous === null) return null;

  if (kind === 'percent') {
    if (previous === 0) return null;
    const ratio = Math.round(((value - previous) / previous) * 1000) / 1000;
    const text = new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      signDisplay: 'exceptZero',
    }).format(ratio);
    return { text: signed(text), trend: trendOf(ratio) };
  }

  if (kind === 'points') {
    const points = Math.round((value - previous) * 10) / 10;
    return { text: score(points, locale), trend: trendOf(points) };
  }

  const diff = Math.round(value - previous);
  const text = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
    signDisplay: 'exceptZero',
  }).format(diff);
  return { text: signed(text), trend: trendOf(diff) };
}

/** A bar's fill as a fraction of the longest bar in its list. Geometry only:
 *  no figure on screen is this number. */
export function fractionsOf(values: readonly (number | null)[]): (number | undefined)[] {
  const max = Math.max(0, ...values.filter((value): value is number => value !== null));
  return values.map((value) => (value === null || max === 0 ? undefined : value / max));
}
